import React from "react";
import { Column, MTableToolbar } from "@material-table/core";
import { SaveAlt } from "@mui/icons-material";
import { alpha, Box, Divider, IconButton, Tooltip, Typography } from "@mui/material";
import TableIconButton from "src/app/components/Table/TableIconButton";
import _ from "lodash";
import { ToolbarComponents } from "src/app/components/Table";

// exports material table to csv file and downloads it
const exportToCsv = (columns: Column<any>[], data: object[]) => {
  // convert to rtl
  columns.reverse();

  let csv = data
    .map((row) =>
      columns.map((column) => {
        // @ts-ignore
        const value = _.get(row, column.field);
        return value;
      })
    )
    .map((row) => row.join(","))
    .join("\n");
  const columnsTitles = columns.map((column) => column.title);
  csv = columnsTitles.join(",") + "\n" + csv;

  // add UTF-8 BOM to beginning so excel doesn't get confused.
  var BOM = String.fromCharCode(0xfeff);
  csv = BOM + csv;

  const csvFile = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const downloadLink = document.createElement("a");
  downloadLink.download = "export.csv";
  downloadLink.href = window.URL.createObjectURL(csvFile);
  downloadLink.click();
};

type Props = {
  data: any[];
  columns: Column<any>[];
  tableTitle: string;
  MaterialTableProps: any;
  components?: ToolbarComponents;
  isWithExport?: boolean;
  selectionLabel?: (count: number) => string;
};

const TableToolbar: React.FC<Props> = ({
  data,
  columns,
  MaterialTableProps,
  components,
  isWithExport,
  selectionLabel = (count) => `${count} נבחרו`,
}) => {
  const selectedRows: any[] = MaterialTableProps?.selectedRows ?? [];
  const isSelectionActive = selectedRows.length > 0;
  const bulkActions: any[] = (MaterialTableProps?.actions ?? []).filter(
    (action: any) => action && action.position === "toolbarOnSelect"
  );

  // When rows are selected, replace the default (untranslated, cluttered)
  // toolbar with a clean, intentional selection bar.
  if (isSelectionActive) {
    return (
      <div>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mx: 2,
            mt: 2,
            px: 2,
            py: 1,
            borderRadius: 1.5,
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
            border: (theme) =>
              `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, color: "primary.main" }}
          >
            {selectionLabel(selectedRows.length)}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {bulkActions.map((action, index) => {
              const Icon =
                typeof action.icon === "function" ? action.icon() : action.icon;
              return (
                <Tooltip key={index} title={action.tooltip ?? ""}>
                  <IconButton
                    color="error"
                    onClick={(event) => action.onClick(event, selectedRows)}
                  >
                    {Icon}
                  </IconButton>
                </Tooltip>
              );
            })}
          </Box>
        </Box>
        <Divider sx={{ mt: 2 }} />
      </div>
    );
  }

  return (
    <div>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          gap: 1,
          pt: 2,
          pr: 2,
        }}
      >
        <MTableToolbar {...MaterialTableProps} />
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: { xs: "flex-end", sm: "flex-start" },
            gap: 1,
          }}
        >
          {components?.right && components?.right}
          {isWithExport && (
            <TableIconButton
              tooltip="יצוא"
              onClick={() => exportToCsv(columns, data)}
              sx={{ mr: 1 }}
            >
              <SaveAlt />
            </TableIconButton>
          )}
          {components?.left && components?.left}
        </Box>
      </Box>
      <Divider sx={{ mt: 2 }} />
    </div>
  );
};

export default TableToolbar;
